<script setup lang="ts" generic="T">
import type { FavoriteList } from '~/models/Favorites'
import type { ContentFile } from '~/models/Content/ContentFile'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { ButtonColors, ButtonShades, ButtonVariants } from '~/components/ui/Button.vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faDownload, faEllipsis } from '@fortawesome/duotone-regular-svg-icons'
import { useAuthStore } from '~/stores/auth'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useStaticProduct } from '~/composables/useStaticProduct'
import useLanguageMap from '~/composables/useLanguageMap'
import { useContentHelper } from '~/composables/useContentHelper'
import Button from '~/components/ui/Button.vue'
import AuPopover from '~/components/ui/AuPopover.vue'
import FavoriteDialog from '~/components/favorites/dialogs/FavoriteDialog.vue'
import ShareContentDialog from '~/components/dialogs/ShareContentDialog.vue'
import LearningPathDialog from '~/components/dialogs/LearningPathDialog.vue'
import CopyUrlButton from '~/components/cards/buttons/CopyUrlButton.vue'

const props = withDefaults(defineProps<{
  resource?: BaseItem
  favoriteList?: FavoriteList
  toggleVariant?: ButtonVariants
  buttonClasses?: string
  direction?: 'top' | 'bottom' | 'left' | 'right'
  toggleColor?: ButtonColors | string
  toggleShade?: ButtonShades
}>(), {
  toggleVariant: 'ghost',
  toggleShade: 'light',
  direction: 'top'
})

const { t } = useI18n()
const { isTeacher, hasOrganization } = storeToRefs(useAuthStore())
const { isPackage, isFile, isLearningPath } = useContentHelper()
const { buildFileUrl, buildResourceUrl } = useUrlBuilder()
const { reverseMap } = useLanguageMap()
const { Terminprover } = useStaticProduct()

const isExam = computed(() => props.resource && props.resource.pathString.startsWith(Terminprover.aunivers.pathString!))
const isShareableItem = computed(() => props.resource && !isExam.value && !isPackage(props.resource))
const isShareableList = computed(() => props.favoriteList && isTeacher.value)
const isShareable = computed(() => isShareableItem.value || isShareableList.value)

const showShareButton = computed(() => isTeacher.value && hasOrganization.value)
const showLearningPathButton = computed(() => props.resource && isLearningPath(props.resource))
const showFavoriteButton = computed(() => isTeacher.value)
const showCopyButton = computed(() => isTeacher.value)

const fileLanguageCodes = computed(() => {
  if (!props.resource || !isFile(props.resource))
    return []
  return (props.resource).languageCodes.map(code => (reverseMap[code]))
})

function getDownloadLabel(language: string) {
  if (fileLanguageCodes.value.length < 2) {
    return t('download')
  }
  return t('downloadIn', { in: t(`metadata.language.${language}`).toLowerCase() })
}

const showCardMenu = computed(() => isShareable.value && (
  showCopyButton.value
  || showFavoriteButton.value
  || showShareButton.value
  || showLearningPathButton.value
  || fileLanguageCodes.value.length > 0
))
</script>

<template>
  <AuPopover
    v-if="showCardMenu"
    :side="direction"
    align="end"
  >
    <template #trigger>
      <slot name="toggle">
        <Button
          shape="square"
          :shade="toggleShade"
          :class="buttonClasses"
          :color="toggleColor"
          :variant="toggleVariant"
        >
          <font-awesome-icon :icon="faEllipsis" />
        </Button>
      </slot>
    </template>
    <template #content>
      <slot
        name="menu"
        :item="resource"
      >
        <ShareContentDialog
          v-if="showShareButton"
          :resource="resource"
          variant="ghost"
          show-label
          size="lg"
          full-width
        />
        <LearningPathDialog
          v-if="showLearningPathButton && resource"
          :resource="resource"
          variant="ghost"
          size="lg"
          full-width
          show-label
        />
        <Button
          v-for="language in fileLanguageCodes"
          :key="`file-${language}`"
          variant="ghost"
          size="lg"
          full-width
          :href="buildFileUrl(resource as ContentFile, language)"
        >
          <font-awesome-icon :icon="faDownload" />
          {{ getDownloadLabel(language) }}
        </Button>
        <CopyUrlButton
          v-if="showCopyButton && props.resource"
          :url="buildResourceUrl(props.resource)"
        />
        <slot name="favorite">
          <FavoriteDialog
            v-if="showFavoriteButton"
            action="addTo"
            :item="resource"
          />
        </slot>
      </slot>
    </template>
  </AuPopover>
  <div
    v-else
    class="size-8.5 shrink-0"
  />
</template>
